import {
  AllCommunityModule,
  ColDef,
  ColumnState,
  GridReadyEvent,
  ModuleRegistry
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import { useDugoutOrganizations } from '@/hooks/useDugoutOrganizations';
import type { OrganizationDetail } from '@/types/organization';
import { Input, Paginator } from '@/ui';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import NotAuthorized from '@/ui/components/NotAuthorized';
import {
  type FilterModel,
  type SortModelItem,
  filterModelFromSearch,
  gridStateToSearch,
  pageFromSearch,
  pageToSearch,
  sortModelFromSearchWith,
  translateFieldCondition,
  translateFilterModelWith,
  translateSortModelWith
} from '@/utils/gridFilters';
import { resolveGridTheme } from '@/utils/gridTheme';
import { hasPermission, ORGANIZATION_VIEW } from '@/utils/userPermission';

import { createColumnDefs, DEFAULT_ORDERING, ORGANIZATION_GRID_FIELDS } from './columns';

ModuleRegistry.registerModules([AllCommunityModule]);

const SEARCH_PARAM = 'search';

const translateCondition = (field: string, filter: FilterModel[string]) =>
  translateFieldCondition(ORGANIZATION_GRID_FIELDS, field, filter);

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Staff list of every organization (including ones hidden from the public
 * ROI/referral pickers), replacing the Django admin changelist. Filtering and
 * sorting run server-side; the grid's filter, sort and page live in the URL so
 * a view reloads and shares as a link.
 */
const OrganizationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme, darkStyle } = useContext(AppContext);
  const gridRef = useRef<AgGridReact<OrganizationDetail>>(null);

  const gridTheme = useMemo(
    () => resolveGridTheme(resolvedTheme, darkStyle),
    [resolvedTheme, darkStyle]
  );

  const search = searchParams.toString();
  const filterModel = useMemo(() => filterModelFromSearch(search), [search]);
  const sortModel = useMemo(
    () => sortModelFromSearchWith(ORGANIZATION_GRID_FIELDS.sortable, search),
    [search]
  );
  const page = pageFromSearch(search);
  const searchText = searchParams.get(SEARCH_PARAM) ?? '';

  const requestParams = useMemo(
    () => ({
      page: String(page),
      ordering:
        translateSortModelWith(ORGANIZATION_GRID_FIELDS.sortable, sortModel) ?? DEFAULT_ORDERING,
      ...translateFilterModelWith(translateCondition, filterModel),
      ...(searchText && { search: searchText })
    }),
    [page, sortModel, filterModel, searchText]
  );

  const { data, isPending } = useDugoutOrganizations(requestParams);
  const organizations = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;
  const totalPages = data?.data?.total_pages ?? 0;

  // Any change to what's shown starts back on page 1. Built from the latest
  // params rather than this render's, so a debounced search landing after a
  // grid change doesn't write back the older grid state.
  const updateSearch = (update: (params: string) => string) => {
    setSearchParams((prev) => new URLSearchParams(update(pageToSearch(prev.toString(), 1))));
  };

  const [searchInput, setSearchInput] = useState(searchText);
  useEffect(() => {
    if (searchInput === searchText) return;
    const timeout = setTimeout(() => {
      updateSearch((params) => {
        const next = new URLSearchParams(params);
        if (searchInput) next.set(SEARCH_PARAM, searchInput);
        else next.delete(SEARCH_PARAM);
        return next.toString();
      });
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Back/Forward changes the URL without touching the controls, so they're
  // brought in line with it here. Setting searchInput also cancels a pending
  // debounced search (its effect cleans up), so the restored URL wins.
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const applyUrlStateToGrid = (api: GridReadyEvent<OrganizationDetail>['api']) => {
    if (!sameJson(api.getFilterModel(), filterModel)) api.setFilterModel(filterModel);
    api.applyColumnState({
      state: sortModel.map((item, sortIndex) => ({ ...item, sortIndex })),
      defaultState: { sort: null }
    });
  };

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) applyUrlStateToGrid(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModel, sortModel]);

  const onGridReady = (event: GridReadyEvent<OrganizationDetail>) => applyUrlStateToGrid(event.api);

  // Applying the URL's state to the grid fires these too; they're no-ops then,
  // since the grid already matches the URL, so its page isn't reset.
  const onFilterChanged = () => {
    const next = (gridRef.current?.api.getFilterModel() ?? {}) as FilterModel;
    if (sameJson(next, filterModel)) return;
    updateSearch((params) =>
      gridStateToSearch(
        params,
        next,
        sortModelFromSearchWith(ORGANIZATION_GRID_FIELDS.sortable, params)
      )
    );
  };

  const onSortChanged = () => {
    const columnState: ColumnState[] = gridRef.current?.api.getColumnState() ?? [];
    const next: SortModelItem[] = columnState
      .filter((column): column is ColumnState & { sort: 'asc' | 'desc' } => !!column.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((column) => ({ colId: column.colId, sort: column.sort }));
    if (sameJson(next, sortModel)) return;
    updateSearch((params) => gridStateToSearch(params, filterModelFromSearch(params), next));
  };

  const goToPage = (nextPage: number) =>
    setSearchParams((prev) => new URLSearchParams(pageToSearch(prev.toString(), nextPage)));

  const columnDefs = useMemo(() => createColumnDefs(), []);
  const defaultColDef = useMemo<ColDef>(
    () => ({ sortable: true, resizable: true, filter: true }),
    []
  );

  return (
    <Container type="full-width-page">
      <div className="mb-3">
        <PageHeader headingText="Organizations" />
      </div>

      <div className="bg-white dark:bg-uniform-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-uniform-gray-700 p-4 mb-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input
            id="organizations-search"
            label="Search"
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Name, description, Kustomer ID or email…"
            value={searchInput}
          />
        </div>
        <p className="mt-3 text-sm text-gray-500">
          {isPending ? 'Loading…' : `${totalCount} organization${totalCount === 1 ? '' : 's'}`}
          {' · '}Use the column menus to filter and sort.
        </p>
      </div>

      <Card className="overflow-hidden p-0" paddingClass="p-0">
        <AgGridReact<OrganizationDetail>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          getRowId={(params) => params.data.id}
          loading={isPending}
          onFilterChanged={onFilterChanged}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
          pagination={false}
          rowData={organizations}
        />
      </Card>

      <div className="mt-4">
        <Paginator currentPage={page} totalPages={totalPages} onPageChange={goToPage} />
      </div>
    </Container>
  );
};

const Organizations = () => {
  const { permissions } = useContext(AppContext);
  if (!hasPermission({ permissions, requiredPermission: ORGANIZATION_VIEW })) {
    return <NotAuthorized />;
  }
  return <OrganizationsList />;
};

export default Organizations;
